import React, { useState, useEffect, useRef } from 'react';
import { Typography } from '@mui/material';

// Format time in HH:MM:SS format
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    secs.toString().padStart(2, '0')
  ].join(':');
};

interface TimerDisplayProps {
  startTime: Date | null;
  onTimeUpdate?: (seconds: number) => void;
  fontSize?: string; // Added fontSize prop for customization
}

// Define interface for selection info
interface SelectionInfo {
  start: number | null;
  end: number | null;
}

// Separated Timer component to prevent re-renders of the main form
const TimerDisplay: React.FC<TimerDisplayProps> = React.memo(({ startTime, onTimeUpdate, fontSize = '0.8rem' }) => {
  // Use refs to hold display state instead of React state to prevent re-renders
  const displayTimeRef = useRef<HTMLSpanElement>(null);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const isRunningRef = useRef<boolean>(false);
  const localStorageKey = 'formTimerElapsedTime';
  
  // Track whether we're currently editing a text field
  const isTextFieldFocusedRef = useRef<boolean>(false);
  
  // Helper to check if a text input is currently focused
  function isTextInputFocused(): boolean {
    const activeElement = document.activeElement;
    return (
      activeElement instanceof HTMLInputElement && 
      ['text', 'email', 'search', 'tel', 'url', 'password', ''].includes(activeElement.type)
    );
  }
  
  // Debug logging function
  const debugLog = (...args: any[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[TimerDisplay]', ...args);
    }
  };
  
  // Only run this effect once when component mounts or unmounts
  // This prevents re-renders from resetting the timer
  useEffect(() => {
    if (!startTime) {
      if (displayTimeRef.current) {
        displayTimeRef.current.textContent = '00:00:00';
      }
      return;
    }
    
    // Initial calculation
    isRunningRef.current = true;
    const initialSeconds = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
    
    // Store elapsed time in localStorage instead of calling parent
    localStorage.setItem(localStorageKey, initialSeconds.toString());
    
    // Only call parent update once to initialize, without causing focus issues
    if (onTimeUpdate) {
      // Use setTimeout to avoid immediate state update during render
      setTimeout(() => {
        // Only update parent state if no text field is currently focused
        if (!isTextInputFocused()) {
          debugLog('Initial time update, no focused inputs');
          onTimeUpdate(initialSeconds);
          lastUpdateRef.current = initialSeconds;
        } else {
          debugLog('Skipping initial time update, text field is focused');
        }
      }, 0);
    }
    
    // Initial display
    if (displayTimeRef.current) {
      displayTimeRef.current.textContent = formatTime(initialSeconds);
    }
    
    // Setup event listeners to track text field focus
    const handleFocusIn = () => {
      const wasFocused = isTextFieldFocusedRef.current;
      isTextFieldFocusedRef.current = isTextInputFocused();
      
      if (wasFocused !== isTextFieldFocusedRef.current) {
        debugLog('Text field focus changed:', isTextFieldFocusedRef.current);
      }
    };
    
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusIn, true);
    
    // Immediately check if a text field is already focused
    isTextFieldFocusedRef.current = isTextInputFocused();
    
    // Direct DOM updates for the timer to prevent React re-renders
    function updateTimerDisplay() {
      if (!isRunningRef.current || !startTime) return;
      
      // Update the element directly without setState
      if (displayTimeRef.current) {
        const elapsedSeconds = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);
        displayTimeRef.current.textContent = formatTime(elapsedSeconds);
        
        // Save to localStorage regularly without affecting React state
        localStorage.setItem(localStorageKey, elapsedSeconds.toString());
        
        // Update parent state every 5 seconds, regardless of focus state
        if (onTimeUpdate && (elapsedSeconds - lastUpdateRef.current >= 5)) {
          lastUpdateRef.current = elapsedSeconds;
          
          // Update parent with minimal impact
          requestAnimationFrame(() => {
            onTimeUpdate(elapsedSeconds);
          });
        }
      }
      
      // Schedule next update - still use a longer interval when text fields are focused
      // but not so long that the timer appears frozen
      const updateInterval = isTextFieldFocusedRef.current ? 5000 : 1000;
      timerIdRef.current = setTimeout(updateTimerDisplay, updateInterval);
    }
    
    // Start the timer
    timerIdRef.current = setTimeout(updateTimerDisplay, 1000);
    
    // Cleanup on unmount
    return () => {
      isRunningRef.current = false;
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
      }
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusIn, true);
    };
  }, [startTime, onTimeUpdate]); // Only re-run if startTime changes, not on every render
  
  // Return a static element that we'll update directly with DOM manipulation
  return (
    <Typography 
      variant="body2" 
      fontSize={fontSize} // Use the passed fontSize prop instead of hardcoded value
      fontWeight="bold"
      tabIndex={-1} // Prevent tab focus
      style={{ pointerEvents: 'none' }} // Prevent mouse interaction
    >
      <span ref={displayTimeRef}>00:00:00</span>
    </Typography>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  // Only re-render if startTime changes from null to a value or vice versa
  if ((!prevProps.startTime && nextProps.startTime) || 
      (prevProps.startTime && !nextProps.startTime)) {
    return false; // Different, should re-render
  }
  return true; // Same, no need to re-render
});

export default TimerDisplay; 